import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath } from 'react-router';
import {
  CSSVarStyles,
  newShade,
  OnBoardingScreenOptions,
  OnBoardingScreensType,
  OnboardingVariant,
} from '@mindly/ui-components';
import {
  experimentVariantSelector,
  flowCurrentScreenIdSelector,
  flowIsTestingSelector,
  isLoadingFlowSelector,
  onBoardingFlowSelector,
} from '../../redux/onBoardingFlow/selector';
import {
  setAnswer,
  setErrorMessage,
  setCurrentScreenId as setCurrentScreenIdAction,
  updateScreensHistory,
  setProgress,
  setGoalScreenData,
} from '../../redux/onBoardingFlow/reducer';
import { PublicRoutes } from '../../routes/navigation';
import { setSplashColor } from '../utils/splashColor';
import { Style } from '@capacitor/status-bar';
import { ONBOARDING_THEME_DEFAULT_COLORS } from '@mindly/ui-components';

const ONBOARDING_THEME_DEFAULT_COLORS_LIST = Object.values(ONBOARDING_THEME_DEFAULT_COLORS).map(
  (theme) => theme.defaultThemeColor
);

type UseOnBoardingFlowActionType = {
  getNextURL: (answer?: string[], forceRedirect?: boolean) => string;
  currentScreenData: OnBoardingScreensType | null;
  currentBgColor: string;
  buttonsStyles: CSSVarStyles;
  subgoalButtonsStyles: CSSVarStyles;
  skipButtonStyles: CSSVarStyles;
};

const SINGLE_SELECTION_SCREEN_OPTIONS = [
  OnBoardingScreenOptions.SINGLE_SELECT,
  OnBoardingScreenOptions.SINGLE_IMAGE_SELECT,
  OnBoardingScreenOptions.SINGLE_ROUND_IMAGE_SELECT,
  OnBoardingScreenOptions.SINGLE_SCALE_SELECT,
];

export const useOnBoardingFlowAction = (screenId?: string): UseOnBoardingFlowActionType => {
  const dispatch = useDispatch();
  const flowData = useSelector(onBoardingFlowSelector);
  const isLoading = useSelector(isLoadingFlowSelector);
  const isTestMode = useSelector(flowIsTestingSelector);
  const selectedScreenId = useSelector(flowCurrentScreenIdSelector);
  const selectedExperimentVariant = useSelector(experimentVariantSelector);

  const [currentScreenData, setCurrentScreenData] = useState<OnBoardingScreensType | null>(null);
  const [currentScreenId, setCurrentScreenId] = useState<string | undefined>(screenId);

  const searchParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    if (isLoading) return;
    const logScreenId = screenId ?? 'start';
    console.info(`[FLOW]: Init hook screen ${logScreenId} data`);

    if (!flowData) {
      console.error(`[FLOW]: Init hook screen ${logScreenId} data -> Flow data not found`);
      return;
    }

    const findStartScreenId = (filter: (r: any) => boolean) =>
      Object.keys(flowData.screens).find((s) => {
        const scr = flowData.screens[s];
        return (
          (scr.type === OnBoardingScreenOptions.START || scr.isStartScreen) && Object.values(scr.rules).some(filter)
        );
      });

    let finalScreenId = screenId as string;

    if (!screenId) {
      finalScreenId =
        findStartScreenId((r) => r.experimentId === selectedExperimentVariant?.id) ??
        findStartScreenId((r) => !r.experimentId) ??
        '';

      if (!finalScreenId) {
        finalScreenId =
          Object.keys(flowData.screens).find((s) => {
            const scr = flowData.screens[s];
            return (
              (scr.type === OnBoardingScreenOptions.START || scr.isStartScreen) &&
              (!scr.rules || Object.keys(scr.rules).length === 0)
            );
          }) ?? '';
      }

      if (!finalScreenId) {
        console.error(`[FLOW]: Init hook screen ${logScreenId} data -> Start screen id not found`);
        dispatch(setErrorMessage('Start screen id not found'));
        return;
      }

      localStorage.setItem('currentGoalScreenCount', '0');
      dispatch(
        setGoalScreenData({
          currentGoalScreenCount: 0,
        })
      );

      setCurrentScreenId(finalScreenId);
      dispatch(setProgress({ screenId: finalScreenId }));
      dispatch(setCurrentScreenIdAction(finalScreenId));
      dispatch(updateScreensHistory(finalScreenId));
    }

    const screenData = flowData.screens[finalScreenId];
    if (!screenData) {
      console.error(`[FLOW]: Init hook screen ${logScreenId} data -> Screen data not found`);
      dispatch(setErrorMessage('Screen data not found'));
      return;
    }

    setCurrentScreenData({ ...screenData, id: finalScreenId });
  }, [screenId, flowData, isLoading, selectedExperimentVariant, dispatch]);

  useEffect(() => {
    if (!selectedScreenId) return;
    if (!flowData?.screens[selectedScreenId]) return;

    const selectedScreenData = flowData.screens[selectedScreenId];

    if ('backgroundColor' in selectedScreenData && selectedScreenData.backgroundColor) {
      setSplashColor(selectedScreenData.backgroundColor, Style.Dark);
    } else {
      setSplashColor('#fffffe');
    }
  }, [flowData, selectedScreenId]);

  const getNextURL = useCallback(
    (answer, forceRedirect = false): string => {
      if (!flowData) {
        console.error(`[FLOW]: getNextURL -> Flow data not found`);
        dispatch(setErrorMessage('Flow data not found'));
        return '/';
      }

      if (forceRedirect) {
        return isTestMode
          ? generatePath(PublicRoutes.AUTH + `?isWebToWebAuth=${flowData?.isWebToWeb ?? false}&flowId=${flowData?.id}`)
          : generatePath(PublicRoutes.ON_BOARDING_FLOW_REDIRECT, { flowId: flowData.id }) +
              `?${searchParams.toString()}`;
      }

      const defaultURL =
        generatePath(PublicRoutes.ON_BOARDING_FLOW, { flowId: flowData.id }) + `?${searchParams.toString()}`;

      if (!currentScreenId) {
        console.error(`[FLOW]: getNextURL -> Current screen id not found`);
        dispatch(setErrorMessage('Current screen id not found'));
        return defaultURL;
      }

      if (!currentScreenData) {
        console.error(`[FLOW]: getNextURL -> Current screen data not found`);
        dispatch(setErrorMessage('Current screen data not found'));
        return defaultURL;
      }

      if (!currentScreenData.rules) {
        console.error(`[FLOW]: getNextURL -> Current screen rules not found`);
        dispatch(setErrorMessage('Current screen rules not found'));
        return defaultURL;
      }

      if (!!answer && (!!screenId || !!currentScreenId)) {
        dispatch(setAnswer({ screenId: screenId ?? currentScreenId, answerIds: answer }));
      }

      if (!SINGLE_SELECTION_SCREEN_OPTIONS.includes(currentScreenData.type)) {
        // First try to find a rule that matches the experiment
        let nextScreenId = Object.values(currentScreenData.rules).find(
          (r) => r?.experimentId === selectedExperimentVariant?.id
        )?.action?.target;

        // If no experiment-specific rule found, use default (non-experiment) rule
        if (!nextScreenId) {
          nextScreenId = Object.values(currentScreenData.rules).find((r) => !r.experimentId)?.action?.target;
        }

        if (nextScreenId) {
          console.info(`[FLOW]: getNextURL -> Next screen id ${nextScreenId} found`);
          return (
            generatePath(PublicRoutes.ON_BOARDING_FLOW_SCREEN, { flowId: flowData.id, screenId: nextScreenId }) +
            `?${searchParams.toString()}`
          );
        } else {
          localStorage.removeItem('currentGoalScreenCount');

          console.info(`[FLOW]: getNextURL -> Next screen id not found, go to end flow`);
          return isTestMode
            ? generatePath(PublicRoutes.AUTH) +
                `?${searchParams.toString()}&isWebToWebAuth=${flowData?.isWebToWeb ?? false}&flowId=${flowData?.id}`
            : generatePath(PublicRoutes.ON_BOARDING_FLOW_REDIRECT, { flowId: flowData.id }) +
                `?${searchParams.toString()}`;
        }
      } else {
        let nextScreenId = Object.values(currentScreenData.rules).find(
          (r) => r.value === 'all' && (!r.experimentId || r.experimentId === selectedExperimentVariant?.id)
        )?.action?.target;

        if (nextScreenId) {
          console.info(`[FLOW]: getNextURL -> Next screen id ${nextScreenId} found for all in single select`);
          return (
            generatePath(PublicRoutes.ON_BOARDING_FLOW_SCREEN, { flowId: flowData.id, screenId: nextScreenId }) +
            `?${searchParams.toString()}`
          );
        } else if (answer) {
          // First try to find a rule that matches both the answer and experiment
          nextScreenId = Object.values(currentScreenData.rules).find(
            (r) => r.value === answer[0] && r.experimentId === selectedExperimentVariant?.id
          )?.action?.target;

          // If no matching rule with experiment, fall back to just matching the answer
          if (!nextScreenId) {
            nextScreenId = Object.values(currentScreenData.rules).find((r) => r.value === answer[0] && !r.experimentId)
              ?.action?.target;
          }

          if (nextScreenId) {
            console.info(
              `[FLOW]: getNextURL -> Next screen id ${nextScreenId} found for selected answer in single select`
            );
            return (
              generatePath(PublicRoutes.ON_BOARDING_FLOW_SCREEN, { flowId: flowData.id, screenId: nextScreenId }) +
              `?${searchParams.toString()}`
            );
          } else {
            localStorage.removeItem('currentGoalScreenCount');

            console.info(`[FLOW]: getNextURL -> Next screen id not found, go to end flow`);

            return isTestMode
              ? generatePath(
                  PublicRoutes.AUTH + `?isWebToWebAuth=${flowData?.isWebToWeb ?? false}&flowId=${flowData?.id}`
                )
              : generatePath(PublicRoutes.ON_BOARDING_FLOW_REDIRECT, { flowId: flowData.id }) +
                  `?${searchParams.toString()}`;
          }
        }
      }

      return defaultURL;
    },
    [flowData, currentScreenId, currentScreenData, dispatch, isTestMode, screenId, selectedExperimentVariant]
  );

  const currentBgColor: string = useMemo(() => {
    if (!currentScreenData) return '#fffffe';

    if ('backgroundColor' in currentScreenData && currentScreenData.backgroundColor) {
      return currentScreenData.backgroundColor;
    }

    return '#fffffe';
  }, [currentScreenData]);

  const isCustomTheme = useMemo(() => {
    return flowData?.theme_v2?.template === OnboardingVariant.custom;
  }, [flowData]);

  const buttonsStyles = useMemo(
    () =>
      (currentScreenData
        ? {
            ...(isCustomTheme && {
              '--background': flowData?.theme_v2?.customColor ?? '',
              '--background-hover': newShade(flowData?.theme_v2?.customColor ?? '', 10),
              '--background-activated': newShade(flowData?.theme_v2?.customColor ?? '', 10),
              '--color': flowData?.theme_v2?.customTextColor ?? '',
            }),
            ...('buttonColor' in currentScreenData &&
              !ONBOARDING_THEME_DEFAULT_COLORS_LIST.includes(currentScreenData.buttonColor ?? '') && {
                '--background': currentScreenData.buttonColor,
                '--background-hover': newShade(currentScreenData.buttonColor as string, 10),
                '--background-activated': newShade(currentScreenData.buttonColor as string, 10),
              }),
            ...('buttonTextColor' in currentScreenData &&
              currentScreenData.buttonTextColor !== '#FFFFFF' && {
                '--color': currentScreenData.buttonTextColor,
              }),
          }
        : {}) as CSSVarStyles,
    [currentScreenData, flowData, isCustomTheme]
  );

  const subgoalButtonsStyles = useMemo(
    () =>
      (currentScreenData
        ? {
            ...(isCustomTheme && {
              '--background': flowData?.theme_v2?.customColor ?? '',
              '--background-hover': newShade(flowData?.theme_v2?.customColor ?? '', 10),
              '--background-activated': newShade(flowData?.theme_v2?.customColor ?? '', 10),
              '--color': flowData?.theme_v2?.customTextColor ?? '',
            }),
            ...('subgoalButtonColor' in currentScreenData &&
              !ONBOARDING_THEME_DEFAULT_COLORS_LIST.includes(currentScreenData.subgoalButtonColor ?? '') && {
                '--background': currentScreenData.subgoalButtonColor,
                '--background-hover': newShade(currentScreenData.subgoalButtonColor as string, 10),
                '--background-activated': newShade(currentScreenData.subgoalButtonColor as string, 10),
              }),
            ...('subgoalButtonTextColor' in currentScreenData &&
              currentScreenData.subgoalButtonTextColor !== '#FFFFFF' && {
                '--color': currentScreenData.subgoalButtonTextColor,
              }),
          }
        : {}) as CSSVarStyles,
    [currentScreenData, flowData, isCustomTheme]
  );

  const skipButtonStyles = useMemo(
    () =>
      (currentScreenData
        ? {
            ...(isCustomTheme && {
              '--color': flowData?.theme_v2?.customTextColor ?? '',
            }),
            ...('buttonColor' in currentScreenData &&
              !ONBOARDING_THEME_DEFAULT_COLORS_LIST.includes(currentScreenData.buttonColor ?? '') && {
                '--color': currentScreenData.buttonColor,
              }),
          }
        : {}) as CSSVarStyles,
    [currentScreenData, flowData, isCustomTheme]
  );

  return {
    getNextURL,
    currentScreenData,
    currentBgColor,
    buttonsStyles,
    subgoalButtonsStyles,
    skipButtonStyles,
  };
};
